import React, {Dispatch, SetStateAction, useCallback, useEffect, useState} from 'react';
import {useConfirmationDialog} from "../../../../../hooks/useConfirmationDialog";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  TextField as MUITextField,
  Tooltip,
  Typography
} from "@mui/material";
import {ICON_MAS} from "../../../components/SharedIcons";
import CardActions from "../../../components/CardActions";
import {useForm, useWatch} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod/dist/zod";
import {EntityWithIdFields} from "src/lib/itaptec/http_client/src";
import DeleteIcon from "@mui/icons-material/Delete";
import {OperacionNotaEstructuradaCuentaFields, OperacionNotaEstructuradaLimiteFields} from "../types/fields";
import {CuentaProvisional} from "../NuevoSistemaNotaEstructurada";
import {ITableColumn, TableList} from "../../../../../components/table";
import {cuentaProvisional, CuentaProvisionalFields} from "../../contraparte/NuevoSistemaContraparte";
import {v4 as uuid} from "uuid";
import {TextField} from "src/lib/itaptec/components_forms/src";
import {AsyncSelect} from "../../../../../components/forms";
import {HttpCacheGeneral} from "../../../../../http/httpCacheGeneral";
import {HttpCacheOperacion} from "../../../api/httpCacheOperacion";
import {EntityWithIdAndDescriptionFields, Moneda, MonedaFields} from "../../../../../types";

interface SeccionNotaEstructuradaDetalleNuevoProps {
  codPais: number,
  cuentasProvisionales?: CuentaProvisional[],
  setCuentasProvisionales: Dispatch<SetStateAction<CuentaProvisional[]>>;
}


function SeccionNotaEstructuradaCuentaNuevo({
                                              codPais,
                                              cuentasProvisionales,
                                              setCuentasProvisionales
                                            }: SeccionNotaEstructuradaDetalleNuevoProps) {
  const [monedas, setMonedas] = useState<Moneda[]>([])
  const {askConfirmation} = useConfirmationDialog()

  const [openDialog, setOpenDialog] = useState<boolean>(false)

  useEffect(() => {
    HttpCacheGeneral.getMonedas().then(setMonedas)
  }, [])

  //(monedas.find((moneda) => moneda[EntityWithIdFields.Id] === codMoneda))
  // [CuentaProvisionalFields.CodMoneda]

  const columns: ITableColumn[] = [
    {
      label: 'Límite concertación',
      value: CuentaProvisionalFields.ImporteConcertacion,
      width: '33%',
      onRenderCell: (cuenta: CuentaProvisional) => {
        return (
          <Typography>
            {`${monedas.find((moneda) => moneda[EntityWithIdFields.Id] === cuenta[CuentaProvisionalFields.CodMoneda])?.[MonedaFields.Simbolo]}
             ${cuenta[CuentaProvisionalFields.ImporteConcertacion].toString()}`}
          </Typography>
        )
      }
    },
    {
      label: 'DVP',
      value: CuentaProvisionalFields.ImporteDPV,
      width: '33%',
      onRenderCell: (cuenta: CuentaProvisional) => {
        return (
          <Typography>
            {`${monedas.find((moneda) => moneda[EntityWithIdFields.Id] === cuenta[CuentaProvisionalFields.CodMoneda])?.[MonedaFields.Simbolo]}
             ${cuenta[CuentaProvisionalFields.ImporteDPV].toString()}`}
          </Typography>
        )
      }
    },
    {
      label: 'FOP',
      value: CuentaProvisionalFields.ImporteFOP,
      width: '33%',
      onRenderCell: (cuenta: CuentaProvisional) => {
        return (
          <Typography>
            {`${monedas.find((moneda) => moneda[EntityWithIdFields.Id] === cuenta[CuentaProvisionalFields.CodMoneda])?.[MonedaFields.Simbolo]}
             ${cuenta[CuentaProvisionalFields.ImporteFOP].toString()}`}
          </Typography>
        )
      }
    }
  ]

  // const formMethods = useForm<OperacionNotaEstructuradaDetalle>({
  //   resolver: zodResolver(schemaOperacionNotaEstructuradaDetalleInsert),
  //   defaultValues: {
  //     [EntityWithIdFields.Id]: -1,
  //     [OperacionNotaEstructuradaDetalleFields.DescSeccion]: 'Propuesta Comercial'
  //
  //   }
  // })

  const {control, handleSubmit, watch, reset} = useForm<CuentaProvisional>({
    resolver: zodResolver(cuentaProvisional),
    defaultValues: {
      idProvisional: uuid(),
      [CuentaProvisionalFields.CodMoneda]: 2 // USD,
    }
  })

  const watchMoneda = useWatch({control: control, name: CuentaProvisionalFields.CodMoneda})

  const alGuardarCuentaProvisional = (data: CuentaProvisional) => {
    const nuevaCuenta: CuentaProvisional = {
      ...data,
      idProvisional: uuid()
    }

    setCuentasProvisionales(prev => [...prev, nuevaCuenta])
    reset()
    setOpenDialog(false)
  }

  const getLimitesConcertacion = useCallback(() => HttpCacheOperacion.getLimitesConcertacionNotaEstructurada(codPais), [])
  const getLimitesDVP = useCallback(() => HttpCacheOperacion.getLimitesDVPNotaEstructurada(codPais), [])
  const getLimitesFOP = useCallback(() => HttpCacheOperacion.getLimitesFOPNotaEstructurada(codPais), [])

  return (
    <CardActions
      title={'Cuentas'}
      actions={
        <Button variant='contained' color='primary' endIcon={ICON_MAS} onClick={() => setOpenDialog(true)}>
          Nuevo
        </Button>
      }
      colapsable
    >
      {!cuentasProvisionales?.length ? (
        <Alert severity='info'>No se encontraron detalles para esta operación.</Alert>
      ) : (
        <Stack spacing={4}>
          {cuentasProvisionales.map(cuenta => (
            <Card key={cuenta[CuentaProvisionalFields.IdProvisional]}>
              <Box display='flex' justifyContent='flex-end'>
                <Tooltip title='Eliminar'>
                  <IconButton
                    onClick={() =>
                      askConfirmation(() =>
                        setCuentasProvisionales(dts =>
                          dts.filter(
                            dt =>
                              dt.idProvisional !==
                              cuenta[CuentaProvisionalFields.IdProvisional]
                          )
                        )
                      )
                    }
                  >
                    <DeleteIcon color='error'/>
                  </IconButton>
                </Tooltip>
              </Box>
              <CardContent>
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant='body2'>Número de cuenta</Typography>
                      <Typography variant='body1'>
                        {cuenta[CuentaProvisionalFields.NroCuenta]}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant='body2'>Razón social</Typography>
                      <Typography variant='body1'>
                        {cuenta[CuentaProvisionalFields.RazonSocial]}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant='body2'>Moneda</Typography>
                      <Typography variant='body1'>
                        {monedas.find((moneda) => moneda[EntityWithIdFields.Id] === cuenta[CuentaProvisionalFields.CodMoneda])?.[MonedaFields.Simbolo]}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
              <Box>
                <TableList
                  entityList={[cuenta]}
                  columns={columns}
                  isLoading={false}
                  error={false}
                />
              </Box>
            </Card>
          ))}
        </Stack>
      )}
      <Dialog open={openDialog} maxWidth='md' fullWidth>
        <DialogTitle>Nueva cuenta</DialogTitle>
        <DialogContent>
          <Stack spacing={4} pt={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  control={control}
                  name={CuentaProvisionalFields.NroCuenta}
                  label='Nro Cuenta'
                  fullWidth
                  mask={{
                    mask: Number
                  }}
                  size='small'
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  control={control}
                  name={CuentaProvisionalFields.RazonSocial}
                  label='Razón Social'
                  fullWidth
                  mask={{
                    mask: /^[A-Za-z]+[A-Za-z ]*$/
                  }}
                  size='small'
                />
              </Grid>
              <Grid item xs={12} md={4}>

                {/*<AsyncSelect*/}
                {/*  control={control}*/}
                {/*  name={CuentaProvisionalFields.CodMoneda}*/}
                {/*  label='Moneda'*/}
                {/*  loadOptions={() => HttpCacheGeneral.getMonedas()}*/}
                {/*  fullWidth*/}
                {/*/>*/}
                <MUITextField
                  label='Moneda'
                  value={monedas.find((moneda) => moneda[EntityWithIdFields.Id] === watchMoneda)?.[EntityWithIdAndDescriptionFields.Description]}
                  size={'small'}
                  disabled
                />
              </Grid>
            </Grid>
            <InputLabel>Límites</InputLabel>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <AsyncSelect
                  control={control}
                  name={OperacionNotaEstructuradaCuentaFields.ImporteConcertacion}
                  label='Límite de Concertación'
                  valueField={OperacionNotaEstructuradaLimiteFields.LimiteImporte}
                  loadOptions={getLimitesConcertacion}
                  fullWidth
                  selectNotNulleable
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <AsyncSelect
                  control={control}
                  name={OperacionNotaEstructuradaCuentaFields.ImporteDPV}
                  label='Límite de DVP'
                  valueField={OperacionNotaEstructuradaLimiteFields.LimiteImporte}
                  loadOptions={getLimitesDVP}
                  fullWidth
                  selectNotNulleable
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <AsyncSelect
                  control={control}
                  name={OperacionNotaEstructuradaCuentaFields.ImporteFOP}
                  label='Límite de DVP'
                  valueField={OperacionNotaEstructuradaLimiteFields.LimiteImporte}
                  loadOptions={getLimitesFOP}
                  fullWidth
                  selectNotNulleable
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant='text' color='primary' onClick={() => setOpenDialog(false)}>
            Cancelar
          </Button>
          <Button variant='contained' color='primary' onClick={handleSubmit(alGuardarCuentaProvisional)}>
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
    </CardActions>
  );
}

export default SeccionNotaEstructuradaCuentaNuevo;